import type { SupabaseClient } from "@supabase/supabase-js";

// Password completion for an officer who landed here from an Auth invite / recovery link. The
// link itself established the session; without a password the officer could never sign in again,
// so this step is required rather than skippable.
//
// Same failure discipline as sign-in (auth.ts): the provider's message is not surfaced, except for
// the two cases that tell the officer something they can actually act on.

export const MIN_PASSWORD_LENGTH = 10;

export const SAFE_SET_PASSWORD_FAILURE = "Could not save that password. Try a different one.";
export const PASSWORD_REUSED_FAILURE = "Choose a password you have not used on this account before.";
export const PASSWORD_TOO_WEAK_FAILURE = "That password is too easy to guess. Use a longer one.";

export type ValidateNewPasswordResult = { ok: true } | { ok: false; reason: string };

/**
 * Pre-network checks. Reasons name the problem but never the value, so they are safe to render
 * inline. Length is the only strength rule we enforce client-side - the project's Auth policy is
 * the real arbiter and its refusal is mapped below.
 */
export function validateNewPassword(input: { password: string; confirmation: string }): ValidateNewPasswordResult {
  if (typeof input.password !== "string" || input.password.length === 0) {
    return { ok: false, reason: "Password is required." };
  }
  if (input.password.length < MIN_PASSWORD_LENGTH) {
    return { ok: false, reason: `Use at least ${MIN_PASSWORD_LENGTH} characters.` };
  }
  if (input.password.trim().length === 0) {
    return { ok: false, reason: "Password cannot be only spaces." };
  }
  if (input.password !== input.confirmation) {
    return { ok: false, reason: "Both passwords must match." };
  }
  return { ok: true };
}

/** Map an Auth failure to safe copy. Anything unrecognised collapses to the generic sentence. */
export function mapSetPasswordFailure(error: unknown): string {
  const text = readErrorText(error).toLowerCase();
  if (text.includes("different from the old password") || text.includes("same_password")) {
    return PASSWORD_REUSED_FAILURE;
  }
  if (text.includes("weak_password") || text.includes("pwned") || text.includes("too weak")) {
    return PASSWORD_TOO_WEAK_FAILURE;
  }
  return SAFE_SET_PASSWORD_FAILURE;
}

export type SetPasswordResult = { ok: true } | { ok: false; reason: string };

/** Store the officer's chosen password on the session established by the invite link. */
export async function setAccountPassword(client: SupabaseClient, password: string): Promise<SetPasswordResult> {
  try {
    const { error } = await client.auth.updateUser({ password });
    if (error !== null) {
      return { ok: false, reason: mapSetPasswordFailure(error) };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SAFE_SET_PASSWORD_FAILURE };
  }
}

function readErrorText(error: unknown): string {
  if (typeof error === "string") return error;
  if (error === null || typeof error !== "object") return "";
  const record = error as { message?: unknown; code?: unknown };
  const message = typeof record.message === "string" ? record.message : "";
  const code = typeof record.code === "string" ? record.code : "";
  return `${message} ${code}`;
}
